import { FC, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import thaprachanMenu from '../data/thaprachanMenu';
import { dishes } from '../data/dishes';
import { CRAWLABLE_SECTIONS } from '../data/menuLookup';

/**
 * Menu ids that have a dish page of their own. The menu is the highest-traffic
 * page on the site, so it is where the dish pages earn their internal links.
 */
/** Menu sections with a landing page of their own. */
const SECTION_LANDING_PAGE: Record<string, { to: string; label: string }> = {
  'Street Food': {
    to: '/thai-street-food-seattle',
    label: 'Read about Thai street food in Seattle →',
  },
};

const DISH_PAGE_BY_MENU_ID: Record<string, string> = dishes.reduce(
  (acc, dish) => {
    const menuId = dish.slug === 'drunken-noodles' ? 'kee-mao'
      : dish.slug === 'pad-see-ew' ? 'pad-se-ew'
      : dish.slug;
    acc[menuId] = dish.slug;
    return acc;
  },
  {} as Record<string, string>,
);

const easing: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

const Menu: FC = () => {
  const [activeCategory, setActiveCategory] = useState(thaprachanMenu[0].title);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const btn = document.getElementById(`tab-${activeCategory}`);
    if (btn && navRef.current) {
      const container = navRef.current;
      const left = btn.offsetLeft - container.offsetWidth / 2 + btn.offsetWidth / 2;
      container.scrollTo({ left, behavior: 'smooth' });
    }
  }, [activeCategory]);

  /**
   * Sections mounted into the DOM: the priority set (always, so they are
   * crawlable without a click) plus whichever tab is currently active.
   */
  const sectionsToRender = thaprachanMenu.filter(
    (c) =>
      (CRAWLABLE_SECTIONS as readonly string[]).includes(c.title) || c.title === activeCategory,
  );

  return (
    <div>
      {/* ── Hero ── same bg images + gradient as landing page hero */}
      <section className="relative bg-black overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/images/Hero Background Photos/optimized/IMG_8254.webp"
            alt=""
            className="w-full h-full object-cover object-center"
          />
          {/* Same gradient overlay used on landing page */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/65 to-black/75" />
        </div>

        {/* Was py-32 at every width, which pushed the first dish more than a
            full screen down on a phone. */}
        <div className="relative z-10 container mx-auto px-6 lg:px-8 py-20 md:py-32 flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10">
          <div>
            <motion.p
              className="text-sm tracking-widest uppercase text-primary/90 font-medium font-body mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: easing }}
            >
              Authentic Thai Cuisine Crafted With Tradition
            </motion.p>
            <motion.h1
              className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white leading-none"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15, ease: easing }}
            >
              Our Menu
            </motion.h1>
          </div>

          <motion.a
            href="https://order.thaprachanthaicuisine.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="self-start md:self-end btn-primary rounded-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: easing }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            Order Online →
          </motion.a>
        </div>
      </section>

      {/* ── Category Tab Bar ── */}
      <div
        ref={navRef}
        className="sticky z-30 bg-neutral-warm border-b border-primary/20 shadow-elegant overflow-x-auto"
        style={{ top: 'var(--header-h)' }}
      >
        <div className="flex min-w-max container mx-auto px-4">
          {thaprachanMenu.map((category) => (
            <button
              id={`tab-${category.title}`}
              key={category.title}
              onClick={() => setActiveCategory(category.title)}
              className={`px-4 md:px-5 py-4 text-[0.8125rem] font-body font-semibold tracking-widest whitespace-nowrap transition-all duration-200 border-b-2 ${
                activeCategory === category.title
                  ? 'border-primary text-primary-ink'
                  : 'border-transparent text-secondary/75 hover:text-secondary hover:border-primary/30'
              }`}
            >
              {category.title.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* ── Menu Content ── bg-background bg-paper-texture matches every other content section */}
      <div className="bg-background bg-paper-texture min-h-screen">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          {/* Each section in sectionsToRender is present in the DOM. The
              priority sections stay mounted so a crawler that never clicks a
              tab still sees them; the rest mount on demand as before. Inactive
              sections are hidden with CSS, which keeps them indexable. */}
          {sectionsToRender.map((section) => {
            const isActive = section.title === activeCategory;
            const landing = SECTION_LANDING_PAGE[section.title];

            return (
              <motion.section
                key={section.title}
                className={isActive ? '' : 'hidden'}
                aria-hidden={!isActive}
                initial={false}
                animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 10 }}
                transition={{ duration: 0.2 }}
              >
                {/* Section label + heading — matches site-wide pattern */}
                <div className="mb-10 md:mb-16">
                  <p className="text-sm tracking-widest uppercase text-primary/90 font-medium font-body mb-4">
                    Our Selection
                  </p>
                  <h2 className="font-heading text-4xl md:text-5xl text-secondary">
                    {section.title}
                  </h2>
                  <div className="mt-3 h-0.5 w-12 bg-primary" />

                  <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 font-body text-sm">
                    {landing && (
                      <Link
                        to={landing.to}
                        className="text-primary-ink underline underline-offset-2 hover:text-secondary"
                      >
                        {landing.label}
                      </Link>
                    )}
                    <Link
                      to="/dishes"
                      className="text-primary-ink underline underline-offset-2 hover:text-secondary"
                    >
                      Our signature dishes →
                    </Link>
                    <Link
                      to="/vegetarian-thai-seattle"
                      className="text-primary-ink underline underline-offset-2 hover:text-secondary"
                    >
                      Vegetarian &amp; tofu dishes →
                    </Link>
                  </div>
                </div>

                {/* Items — staggered entrance, Playfair names for premium feel */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 md:gap-x-20">
                  {section.items.map((item, index) => (
                    <motion.div
                      key={item.id}
                      className="py-5 border-b border-neutral-warm"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.6,
                        delay: Math.min(index * 0.04, 0.35),
                        ease: 'easeOut',
                      }}
                    >
                      <h3 className="font-heading text-base text-secondary mb-1">
                        {DISH_PAGE_BY_MENU_ID[item.id] ? (
                          <Link
                            to={`/dishes/${DISH_PAGE_BY_MENU_ID[item.id]}`}
                            className="underline decoration-primary/50 underline-offset-4 hover:text-primary-ink transition-colors"
                          >
                            {item.name}
                          </Link>
                        ) : (
                          item.name
                        )}
                      </h3>
                      {item.description && (
                        <p className="font-body text-sm text-text/80 leading-relaxed">
                          {item.description}
                        </p>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Menu;
